import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getCourses, createCourse, createReservation, createReservationBatch, updateReservation, deleteReservation, getBatchDateRange } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Phone, Mail, Clock, Calendar as CalendarIcon, CheckCircle, Circle, Edit2, Save, X, Users, Plus, Trash2, CalendarDays, Search } from 'lucide-react';
import { toast } from 'sonner';
import CustomerAutocomplete from '../components/CustomerAutocomplete';
import { useAuth } from '../contexts/AuthContext';
import PaymentSplitField from '../components/PaymentSplitField';

const LOCATIONS = [
  { value: 'san_antolin', label: 'San Antolín' },
  { value: 'san_martin', label: 'San Martín' },
  { value: 'ria_niembro', label: 'Ría de Niembro' },
];

const todayStr = new Date().toISOString().split('T')[0];
const invalidSplit = (splits) => splits && splits.some(s => !s.method || !(parseFloat(s.amount) > 0));

const emptyResForm = {
  multi_day: false,
  date: todayStr,
  date_from: todayStr,
  date_to: todayStr,
  location: 'san_antolin',
  course_id: '',
  new_course_time: '10:00',
  reservation_name: '',
  phone: '',
  email: '',
  dni: '',
  students: [{ name: '' }],
  num_students: 0,
  income_total: '',
  amount_paid: '',
  payment_method: 'cash',
  payment_splits: null,
  notes: '',
  selected_dates: [],
};

const fmtDate = (d) => {
  if (!d) return '';
  const [y, m, day] = d.split('-');
  return `${day}-${m}-${y}`;
};
const getLoc = (v) => LOCATIONS.find(l => l.value === v)?.label || v;
const getDatesInRange = (from, to) => {
  const dates = [];
  let cur = new Date(from + 'T00:00:00Z');
  const end = new Date(to + 'T00:00:00Z');
  while (cur <= end) { dates.push(cur.toISOString().split('T')[0]); cur.setUTCDate(cur.getUTCDate() + 1); }
  return dates;
};

export default function ReservationsPage() {
  const { bizumRecipients } = useAuth();
  const [tab, setTab] = useState('proximas');
  const [allReservations, setAllReservations] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [resDialog, setResDialog] = useState(false);
  const [resForm, setResForm] = useState({ ...emptyResForm });
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  const [calendarDate, setCalendarDate] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [batchSelectDialog, setBatchSelectDialog] = useState(false);
  const [pendingBatchPayload, setPendingBatchPayload] = useState(null);
  const [pendingBatchAmbiguousDays, setPendingBatchAmbiguousDays] = useState([]);
  const [batchDaySelections, setBatchDaySelections] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getCourses({ limit: 500 });
      const allCourses = data.courses || data;
      setCourses(allCourses);
      const reservations = [];
      for (const course of allCourses) {
        for (const res of (course.reservations || [])) {
          reservations.push({
            ...res,
            course_date: course.date,
            course_time: course.time || '',
            course_location: course.location,
            course_observations: course.observations || '',
          });
        }
      }
      setAllReservations(reservations);
    } catch (e) {
      console.error(e);
      toast.error('Error cargando reservas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Courses matching the selected date + location (single-day mode only)
  const coursesForDate = resForm.multi_day
    ? []
    : courses.filter(c => c.date === resForm.date && c.location === resForm.location);

  // When date or location changes: auto-select first available course
  const applyDateOrLocation = (field, value, prev) => {
    const newDate = field === 'date' ? value : prev.date;
    const newLoc = field === 'location' ? value : prev.location;
    const matching = courses.filter(c => c.date === newDate && c.location === newLoc);
    const courseId = matching.length === 1 ? matching[0].id : matching.length > 1 ? matching[0].id : '';
    return { ...prev, [field]: value, course_id: courseId };
  };

  const openNewReservation = () => {
    const initial = { ...emptyResForm };
    // Auto-select if there's a matching course for today+san_antolin
    const matching = courses.filter(c => c.date === todayStr && c.location === 'san_antolin');
    if (matching.length >= 1) initial.course_id = matching[0].id;
    setResForm(initial);
    setResDialog(true);
  };

  const handleResSubmit = async (e) => {
    e.preventDefault();
    if (invalidSplit(resForm.payment_splits)) { toast.error('Completa ambos métodos del pago dividido con un importe mayor que 0'); return; }
    setSubmitting(true);
    try {
      const students = resForm.students.filter(s => s.name.trim());
      const numSt = parseInt(resForm.num_students) || 0;
      const incomeTotal = parseFloat(resForm.income_total) || 0;
      const amountPaid = parseFloat(resForm.amount_paid) || 0;

      if (resForm.multi_day) {
        const batchPayloadRP = {
          date_from: resForm.date_from,
          date_to: resForm.date_to,
          location: resForm.location,
          reservation_name: resForm.reservation_name,
          phone: resForm.phone,
          email: resForm.email,
          dni: resForm.dni,
          students,
          num_students: numSt,
          income_total: incomeTotal,
          amount_paid: amountPaid,
          payment_method: resForm.payment_method,
          payment_splits: resForm.payment_splits,
          notes: resForm.notes,
          specific_dates: resForm.selected_dates?.length > 0 ? resForm.selected_dates : null,
        };
        const allDatesRP = resForm.selected_dates?.length > 0 ? resForm.selected_dates : getDatesInRange(resForm.date_from, resForm.date_to);
        const ambiguousRP = allDatesRP
          .map(d => ({ date: d, options: courses.filter(c => c.date === d && c.location === resForm.location) }))
          .filter(d => d.options.length > 1);
        if (ambiguousRP.length > 0) {
          setPendingBatchPayload(batchPayloadRP);
          setPendingBatchAmbiguousDays(ambiguousRP);
          setBatchDaySelections({});
          setBatchSelectDialog(true);
          setResDialog(false);
          setResForm({ ...emptyResForm });
          return;
        }
        await createReservationBatch(batchPayloadRP);
        toast.success('Reservas creadas en rango de fechas');
      } else {
        let courseId = resForm.course_id;
        if (!courseId || courseId === '__new__') {
          const { data: newCourse } = await createCourse({
            date: resForm.date,
            time: resForm.new_course_time,
            location: resForm.location,
            monitors: [],
          });
          courseId = newCourse.id;
        }
        await createReservation({
          course_id: courseId,
          reservation_name: resForm.reservation_name,
          phone: resForm.phone,
          email: resForm.email,
          dni: resForm.dni,
          students,
          num_students: numSt,
          income_total: incomeTotal,
          amount_paid: amountPaid,
          payment_method: resForm.payment_method,
          payment_splits: resForm.payment_splits,
          notes: resForm.notes,
        });
        toast.success('Reserva creada');
      }
      setResDialog(false);
      setResForm({ ...emptyResForm });
      fetchData();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Error creando reserva');
    } finally {
      setSubmitting(false);
    }
  };

  const addStudent = () => setResForm(f => ({ ...f, students: [...f.students, { name: '' }] }));
  const updateStudent = (i, val) => { const s = [...resForm.students]; s[i] = { name: val }; const cnt = s.filter(st => st.name.trim()).length; setResForm(f => ({ ...f, students: s, num_students: cnt > 0 ? cnt : f.num_students })); };
  const removeStudent = (i) => setResForm(f => { const ns = f.students.filter((_, j) => j !== i); return { ...f, students: ns, num_students: ns.filter(s => s.name.trim()).length || f.num_students }; });

  // ─── Existing contact/edit logic ──────────────────────────────────────────
  const toggleContacted = async (res) => {
    const newContacted = !res.contacted;
    setAllReservations(prev => prev.map(r => r.id === res.id ? { ...r, contacted: newContacted } : r));
    try {
      await updateReservation(res.id, {
        course_id: res.course_id,
        reservation_name: res.reservation_name || '',
        phone: res.phone || '',
        email: res.email || '',
        dni: res.dni || '',
        client_name: res.client_name || '',
        students: res.students || [],
        num_students: res.num_students || 0,
        income_total: res.income_total || 0,
        amount_paid: res.amount_paid || 0,
        payment_method: res.payment_method || 'cash',
        notes: res.notes || '',
        contacted: newContacted,
      });
    } catch (e) {
      setAllReservations(prev => prev.map(r => r.id === res.id ? { ...r, contacted: res.contacted } : r));
      toast.error('Error actualizando');
    }
  };

  const startEdit = async (res) => {
    setEditingId(res.id);
    let batchDateFrom = ''; let batchDateTo = ''; let batchSelectedDates = []; let isMultiDay = false;
    if (res.batch_id) {
      isMultiDay = true;
      try {
        const { data } = await getBatchDateRange(res.batch_id);
        batchDateFrom = data.date_from || '';
        batchDateTo = data.date_to || '';
        batchSelectedDates = data.dates || [];
      } catch (_) {}
    }
    if (!batchDateFrom) {
      batchDateFrom = res.course_date || todayStr;
      batchDateTo = batchDateFrom;
      batchSelectedDates = batchDateFrom ? [batchDateFrom] : [];
    }
    setEditForm({
      reservation_name: res.reservation_name || '',
      phone: res.phone || '',
      email: res.email || '',
      notes: res.notes || '',
      num_students: res.num_students || 0,
      income_total: res.income_total || '',
      amount_paid: res.amount_paid || '',
      payment_method: res.payment_method || 'cash',
      payment_splits: res.payment_splits || null,
      multi_day: isMultiDay,
      date_from: batchDateFrom,
      date_to: batchDateTo,
      selected_dates: batchSelectedDates,
    });
  };

  const cancelEdit = () => { setEditingId(null); setEditForm({}); };

  const handleBatchWithSelections = async () => {
    const allSelected = pendingBatchAmbiguousDays.every(({date}) => batchDaySelections[date]);
    if (!allSelected) { toast.error('Elige un curso para cada día'); return; }
    try {
      await createReservationBatch({ ...pendingBatchPayload, course_ids_by_date: batchDaySelections });
      toast.success('Reservas creadas en rango de fechas');
      setBatchSelectDialog(false);
      fetchData();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };

  const saveEdit = async (res) => {
    if (invalidSplit(editForm.payment_splits)) { toast.error('Completa ambos métodos del pago dividido con un importe mayor que 0'); return; }
    const updatedFields = {
      reservation_name: editForm.reservation_name || res.reservation_name,
      phone: editForm.phone,
      email: editForm.email,
      num_students: parseInt(editForm.num_students) || 0,
      notes: editForm.notes,
      amount_paid: parseFloat(editForm.amount_paid) || 0,
      payment_method: editForm.payment_method || 'cash',
      payment_splits: editForm.payment_splits,
    };
    setAllReservations(prev => prev.map(r => r.id === res.id ? { ...r, ...updatedFields } : r));
    setEditingId(null);
    try {
      const extBatchId = res.batch_id || (Date.now().toString(36) + Math.random().toString(36).slice(2));
      await updateReservation(res.id, {
        course_id: res.course_id,
        reservation_name: updatedFields.reservation_name,
        phone: updatedFields.phone,
        email: updatedFields.email,
        dni: res.dni || '',
        client_name: res.client_name || '',
        students: res.students || [],
        num_students: updatedFields.num_students,
        income_total: res.income_total || 0,
        amount_paid: updatedFields.amount_paid,
        payment_method: updatedFields.payment_method,
        payment_splits: updatedFields.payment_splits,
        notes: updatedFields.notes,
        contacted: res.contacted || false,
        ...(editForm.multi_day ? { batch_id: extBatchId } : {}),
      });
      if (editForm.multi_day && editForm.date_from && editForm.date_to) {
        const editBatchRP = {
          date_from: editForm.date_from,
          date_to: editForm.date_to,
          location: res.course_location,
          reservation_name: updatedFields.reservation_name,
          phone: updatedFields.phone,
          email: updatedFields.email,
          dni: res.dni || '',
          students: res.students || [],
          num_students: updatedFields.num_students,
          income_total: parseFloat(editForm.income_total) || 0,
          amount_paid: 0,
          payment_method: editForm.payment_method || 'cash',
          payment_splits: null,
          notes: updatedFields.notes,
          batch_id: extBatchId,
          specific_dates: editForm.selected_dates?.length > 0 ? editForm.selected_dates : null,
        };
        const allDatesEP = editForm.selected_dates?.length > 0 ? editForm.selected_dates : getDatesInRange(editForm.date_from, editForm.date_to);
        const ambiguousEP = allDatesEP
          .map(d => ({ date: d, options: courses.filter(c => c.date === d && c.location === res.course_location) }))
          .filter(d => d.options.length > 1);
        if (ambiguousEP.length > 0) {
          setPendingBatchPayload(editBatchRP);
          setPendingBatchAmbiguousDays(ambiguousEP);
          const preSelEP = {}; ambiguousEP.forEach(({date}) => { const bc = courses.find(c => c.date === date && (c.reservations||[]).some(r => r.batch_id === extBatchId)); if (bc) preSelEP[date] = bc.id; });
          setBatchDaySelections(preSelEP);
          setBatchSelectDialog(true);
          setEditingId(null);
          setEditForm({});
          return;
        }
        await createReservationBatch(editBatchRP);
        toast.success('Reserva actualizada y ampliada a más días');
      } else {
        toast.success('Reserva actualizada');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error guardando');
      fetchData();
    }
  };

  const handleDelete = async (res) => {
    const msg = res.batch_id
      ? '¿Eliminar esta reserva y todas las del mismo grupo (varios días)?'
      : '¿Eliminar esta reserva?';
    if (!window.confirm(msg)) return;
    try {
      await deleteReservation(res.id);
      toast.success(res.batch_id ? 'Reservas del grupo eliminadas' : 'Reserva eliminada');
      fetchData();
    } catch (e) {
      toast.error('Error eliminando');
    }
  };

  const applyFilters = (list) => list.filter(r => {
    if (calendarDate) {
      const d = `${calendarDate.getFullYear()}-${String(calendarDate.getMonth()+1).padStart(2,'0')}-${String(calendarDate.getDate()).padStart(2,'0')}`;
      if (r.course_date !== d) return false;
    }
    if (dateFrom && r.course_date < dateFrom) return false;
    if (dateTo && r.course_date > dateTo) return false;
    if (search) {
      const q = search.toLowerCase();
      const fields = [r.reservation_name, r.phone, r.email, r.notes, getLoc(r.course_location)].filter(Boolean);
      if (!fields.some(f => String(f).toLowerCase().includes(q))) return false;
    }
    return true;
  });

  const future = applyFilters(allReservations.filter(r => r.course_date >= todayStr))
    .sort((a, b) => a.course_date.localeCompare(b.course_date) || (a.course_time || '').localeCompare(b.course_time || ''));

  const past = applyFilters(allReservations.filter(r => r.course_date < todayStr))
    .sort((a, b) => b.course_date.localeCompare(a.course_date));

  const list = tab === 'proximas' ? future : past;

  if (loading) return (
    <div className="flex items-center justify-center h-64 text-[#8A9BA8]">Cargando reservas...</div>
  );

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-10 bg-[#FCFBF9] pt-4 md:pt-8 pb-3 space-y-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#1A2B3C]" style={{ fontFamily: 'Manrope' }}>Reservas</h1>
            <p className="text-sm text-[#8A9BA8]">{future.length} próximas · {past.length} en historial</p>
          </div>
          <div className="flex items-center flex-wrap gap-2">
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="icon" className={`border-[#E5E0D8] h-9 w-9 ${calendarDate ? 'bg-[#4A6D7C] text-white hover:bg-[#3A5663]' : 'text-[#4A5D6C]'}`}>
                  <CalendarDays className="w-4 h-4"/>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar mode="single" selected={calendarDate} month={calendarMonth} onMonthChange={setCalendarMonth}
                  onSelect={(d) => { setCalendarDate(d); if (d) setCalendarMonth(d); setCalendarOpen(false); }} />
              </PopoverContent>
            </Popover>
            {calendarDate && (
              <Button variant="ghost" size="sm" className="h-7 text-xs text-[#8A9BA8]"
                onClick={() => { setCalendarDate(null); setCalendarMonth(new Date()); }}>
                ✕ {calendarDate.toLocaleDateString('es-ES')}
              </Button>
            )}
            <div className="flex items-center gap-1">
              <Input type="date" value={dateFrom}
                onChange={e => { const v = e.target.value; setDateFrom(v); if (!dateTo || dateTo < v) setDateTo(v); }}
                className="border-[#E5E0D8] h-9 text-sm w-36" />
              {dateFrom && <button onClick={() => setDateFrom('')} style={{border:'none',background:'none',cursor:'pointer',color:'#8A9BA8',fontSize:14,fontWeight:700,padding:'0 2px'}}>×</button>}
              <span className="text-[#8A9BA8] text-xs mx-1">—</span>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="border-[#E5E0D8] h-9 text-sm w-36" />
              {dateTo && <button onClick={() => setDateTo('')} style={{border:'none',background:'none',cursor:'pointer',color:'#8A9BA8',fontSize:14,fontWeight:700,padding:'0 2px'}}>×</button>}
            </div>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-[#8A9BA8]"/>
              <Input placeholder="Buscar..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 border-[#E5E0D8] w-40 h-9" />
            </div>
            <Button onClick={openNewReservation} className="bg-[#81B29A] hover:bg-[#6A9A83] text-white">
              <Plus className="w-4 h-4 mr-2" /><span className="hidden sm:inline">Nueva Reserva</span><span className="sm:hidden">Nueva</span>
            </Button>
          </div>
        </div>
        <div className="flex gap-1 bg-[#F5F2ED] p-1 rounded-lg w-fit">
          <button
            onClick={() => setTab('proximas')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === 'proximas' ? 'bg-white text-[#1A2B3C] shadow-sm' : 'text-[#8A9BA8] hover:text-[#1A2B3C]'}`}
          >
            Próximas Reservas{' '}
            <span className="ml-1 text-xs bg-[#4A6D7C] text-white rounded-full px-2 py-0.5">{future.length}</span>
          </button>
          <button
            onClick={() => setTab('historial')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === 'historial' ? 'bg-white text-[#1A2B3C] shadow-sm' : 'text-[#8A9BA8] hover:text-[#1A2B3C]'}`}
          >
            Historial{' '}
            <span className="ml-1 text-xs bg-[#8A9BA8] text-white rounded-full px-2 py-0.5">{past.length}</span>
          </button>
        </div>
      </div>

      {list.length === 0 ? (
        <div className="text-center py-16 text-[#8A9BA8]">
          {tab === 'proximas' ? 'No hay próximas reservas' : 'No hay reservas pasadas'}
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((res, idx) => (
            <div
              key={res.id}
              className={`bg-white border rounded-xl p-4 shadow-sm transition-all ${(res.contacted || res.confirmed) ? 'border-green-300 bg-green-50/40' : 'border-[#E5E0D8] hover:border-[#4A6D7C]/30'}`}
            >
              {editingId === res.id ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-[#4A6D7C]">#{idx + 1}</span>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => saveEdit(res)} className="bg-[#4A6D7C] hover:bg-[#3A5D6C] text-white h-8">
                        <Save className="w-3 h-3 mr-1" />Guardar
                      </Button>
                      <Button size="sm" variant="ghost" onClick={cancelEdit} className="h-8">
                        <X className="w-3 h-3 mr-1" />Cancelar
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-[#8A9BA8] mb-1 block">Nombre</label>
                      <Input value={editForm.reservation_name} onChange={e => setEditForm(f => ({ ...f, reservation_name: e.target.value }))} />
                    </div>
                    <div>
                      <label className="text-xs text-[#8A9BA8] mb-1 block">Teléfono</label>
                      <Input value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} placeholder="+34..." />
                    </div>
                    <div>
                      <label className="text-xs text-[#8A9BA8] mb-1 block">Email</label>
                      <Input value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} placeholder="email@..." />
                    </div>
                    <div>
                      <label className="text-xs text-[#8A9BA8] mb-1 block">Nº Alumnos</label>
                      <Input type="number" value={editForm.num_students} onChange={e => setEditForm(f => ({ ...f, num_students: e.target.value }))} min="0" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-xs text-[#8A9BA8] mb-1 block">Notas</label>
                      <Input value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} placeholder="Observaciones..." />
                    </div>
                    <div>
                      <label className="text-xs text-[#8A9BA8] mb-1 block">Forma de pago</label>
                      <Select value={editForm.payment_method||'cash'} onValueChange={v => setEditForm(f => ({ ...f, payment_method: v }))} disabled={editForm.payment_splits?.length > 1}>
                        <SelectTrigger className="border-[#E5E0D8]"><SelectValue/></SelectTrigger>
                        <SelectContent>
                          {(bizumRecipients||[]).map(r => <SelectItem key={r.id} value={`bizum_${r.id}`}>Bizum {r.name}</SelectItem>)}
                          <SelectItem value="caixa">Caixa</SelectItem>
                          <SelectItem value="cash">Efectivo/Cash</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="sm:col-span-2">
                      <PaymentSplitField
                        paymentMethod={editForm.payment_method}
                        paymentSplits={editForm.payment_splits}
                        amountPaid={editForm.amount_paid}
                        bizumRecipients={bizumRecipients}
                        onChange={patch => setEditForm(f => ({ ...f, ...patch }))}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#8A9BA8] mb-1 block">Pagado (€)</label>
                      <Input type="number" step="0.01" value={editForm.amount_paid} disabled={editForm.payment_splits?.length > 1} onChange={e => setEditForm(f => ({ ...f, amount_paid: e.target.value }))} className="border-[#E5E0D8] disabled:opacity-60" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={editForm.multi_day||false} onChange={e => { const chk = e.target.checked; setEditForm(f => ({ ...f, multi_day: chk, selected_dates: chk && f.date_from && f.date_to ? getDatesInRange(f.date_from, f.date_to) : [] })); }} className="rounded border-[#E5E0D8]" />
                        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Ampliar reserva a más días</span>
                      </label>
                    </div>
                    {editForm.multi_day && (
                      <>
                        <div>
                          <label className="text-xs text-[#8A9BA8] mb-1 block">Desde</label>
                          <Input type="date" value={editForm.date_from||''} onChange={e => { const v = e.target.value; setEditForm(f => { const t = (!f.date_to || f.date_to < v) ? v : f.date_to; return { ...f, date_from: v, date_to: t, selected_dates: getDatesInRange(v, t) }; }); }} className="border-[#E5E0D8]" />
                        </div>
                        <div>
                          <label className="text-xs text-[#8A9BA8] mb-1 block">Hasta</label>
                          <Input type="date" value={editForm.date_to||''} onChange={e => setEditForm(f => ({ ...f, date_to: e.target.value, selected_dates: getDatesInRange(f.date_from, e.target.value) }))} className="border-[#E5E0D8]" />
                        </div>
                      </>
                    )}
                    {editForm.multi_day && editForm.date_from && editForm.date_to && (
                      <div className="sm:col-span-2 space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Días ({(editForm.selected_dates||[]).length})</label>
                          <div className="flex gap-2 text-xs">
                            <button type="button" onClick={() => setEditForm(f => ({...f, selected_dates: getDatesInRange(f.date_from, f.date_to)}))} className="text-[#4A6D7C] hover:underline">Todos</button>
                            <span className="text-[#8A9BA8]">·</span>
                            <button type="button" onClick={() => setEditForm(f => ({...f, selected_dates: []}))} className="text-[#4A6D7C] hover:underline">Ninguno</button>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {getDatesInRange(editForm.date_from, editForm.date_to).map(d => { const dt = new Date(d + 'T12:00:00'); const wd = dt.toLocaleDateString('es-ES', {weekday:'short'}); const dd = dt.getDate(); const sel = (editForm.selected_dates||[]).includes(d); return <button key={d} type="button" onClick={() => setEditForm(f => { const sd = f.selected_dates||[]; return {...f, selected_dates: sel ? sd.filter(x=>x!==d) : [...sd,d].sort()}; })} className={`px-2 py-1 rounded text-xs font-medium border transition-colors ${sel ? 'bg-[#4A6D7C] text-white border-[#4A6D7C]' : 'bg-gray-100 text-gray-400 border-gray-200 line-through'}`}>{wd} {dd}</button>; })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#4A6D7C] text-white text-sm font-bold flex items-center justify-center">
                    {idx + 1}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                      <div>
                        <h3 className="font-semibold text-[#1A2B3C] text-base leading-tight">{res.reservation_name || '—'}</h3>
                        {res.confirmed && <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium"><CheckCircle className="w-3 h-3"/>Confirmado</span>}
                        <div className="flex flex-wrap gap-2 mt-1">
                          <span className="flex items-center gap-1 text-sm text-[#8A9BA8]">
                            <CalendarIcon className="w-3.5 h-3.5" />{fmtDate(res.course_date)}
                          </span>
                          <span className="flex items-center gap-1 text-sm text-[#8A9BA8]">
                            <Clock className="w-3.5 h-3.5" />{res.course_time || '—'}
                          </span>
                          {res.num_students > 0 && (
                            <span className="flex items-center gap-1 text-xs bg-[#F5F2ED] px-2 py-0.5 rounded-full text-[#4A6D7C] font-medium">
                              <Users className="w-3 h-3" />{res.num_students} alumno{res.num_students !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => startEdit(res)}
                          className="p-1.5 rounded-lg text-[#8A9BA8] hover:text-[#1A2B3C] hover:bg-[#F5F2ED] transition-colors"
                          title="Editar"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(res)}
                          className="p-1.5 rounded-lg text-[#8A9BA8] hover:text-[#E07A5F] hover:bg-red-50 transition-colors"
                          title={res.batch_id ? 'Eliminar grupo (varios días)' : 'Eliminar reserva'}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        {!res.confirmed && (
                        <button
                          onClick={() => toggleContacted(res)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            res.contacted
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-[#F5F2ED] text-[#8A9BA8] hover:bg-[#E5E0D8] hover:text-[#4A6D7C]'
                          }`}
                          title={res.contacted ? 'Marcado como contactado — pulsa para desmarcar' : 'Marcar como contactado'}
                        >
                          {res.contacted
                            ? <><CheckCircle className="w-4 h-4" /><span className="hidden sm:inline">Contactado</span></>
                            : <><Circle className="w-4 h-4" /><span className="hidden sm:inline">Pendiente</span></>
                          }
                        </button>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {res.phone ? (
                        <a
                          href={`tel:${res.phone.replace(/\s/g, '')}`}
                          className="flex items-center gap-1.5 text-sm text-[#4A6D7C] hover:text-[#3A5D6C] font-medium bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg transition-colors"
                        >
                          <Phone className="w-3.5 h-3.5" />{res.phone}
                        </a>
                      ) : (
                        <span className="text-sm text-[#C5C0B8] italic flex items-center gap-1">
                          <Phone className="w-3.5 h-3.5" />Sin teléfono
                        </span>
                      )}

                      {res.email ? (
                        <a
                          href={`mailto:${res.email}`}
                          className="flex items-center gap-1.5 text-sm text-[#7B5EA7] hover:text-[#6A4E96] font-medium bg-purple-50 hover:bg-purple-100 px-3 py-1.5 rounded-lg transition-colors"
                        >
                          <Mail className="w-3.5 h-3.5" />{res.email}
                        </a>
                      ) : (
                        <span className="text-sm text-[#C5C0B8] italic flex items-center gap-1">
                          <Mail className="w-3.5 h-3.5" />Sin email
                        </span>
                      )}
                    </div>

                    {(res.notes || res.course_observations) && (
                      <div className="mt-2 text-sm text-[#6A7B8A] bg-[#F5F2ED] px-3 py-2 rounded-lg">
                        {res.notes && <span><strong>Notas:</strong> {res.notes}</span>}
                        {res.notes && res.course_observations && <span className="mx-2">·</span>}
                        {res.course_observations && <span><strong>Obs.:</strong> {res.course_observations}</span>}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Nueva Reserva Dialog */}
      <Dialog open={resDialog} onOpenChange={setResDialog}>
        <DialogContent className="bg-[#FCFBF9] border-[#E5E0D8] max-w-lg max-h-[90vh] overflow-y-auto w-[92vw] sm:w-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1A2B3C]" style={{ fontFamily: 'Manrope' }}>
              Nueva Reserva
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleResSubmit} className="space-y-4 mt-2">

            {/* Multi-day toggle */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={resForm.multi_day}
                onChange={e => { const chk = e.target.checked; setResForm(f => ({ ...f, multi_day: chk, course_id: '', selected_dates: chk && f.date_from && f.date_to ? getDatesInRange(f.date_from, f.date_to) : [] })); }}
                className="rounded border-[#E5E0D8]"
              />
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Reserva varios días</span>
            </label>

            {resForm.multi_day ? (
              /* ── Multi-day: date range + location ── */
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Desde</Label>
                  <Input type="date" value={resForm.date_from} onChange={e => { const v = e.target.value; setResForm(f => { const t = (!f.date_to || f.date_to < v) ? v : f.date_to; return { ...f, date_from: v, date_to: t, selected_dates: getDatesInRange(v, t) }; }); }} required className="border-[#E5E0D8]" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Hasta</Label>
                  <Input type="date" value={resForm.date_to} onChange={e => setResForm(f => ({ ...f, date_to: e.target.value, selected_dates: getDatesInRange(f.date_from, e.target.value) }))} required className="border-[#E5E0D8]" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Ubicación</Label>
                  <Select value={resForm.location} onValueChange={v => setResForm(f => ({ ...f, location: v }))}>
                    <SelectTrigger className="border-[#E5E0D8]"><SelectValue /></SelectTrigger>
                    <SelectContent>{LOCATIONS.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            ) : (
              /* ── Single day: date + location + course selector ── */
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Fecha</Label>
                    <Input
                      type="date"
                      value={resForm.date}
                      onChange={e => setResForm(prev => applyDateOrLocation('date', e.target.value, prev))}
                      required
                      className="border-[#E5E0D8]"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Ubicación</Label>
                    <Select value={resForm.location} onValueChange={v => setResForm(prev => applyDateOrLocation('location', v, prev))}>
                      <SelectTrigger className="border-[#E5E0D8]"><SelectValue /></SelectTrigger>
                      <SelectContent>{LOCATIONS.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Course selector */}
                <div className="space-y-1">
                  <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Curso</Label>
                  {coursesForDate.length > 0 ? (
                    <Select
                      value={resForm.course_id || coursesForDate[0].id}
                      onValueChange={v => setResForm(f => ({ ...f, course_id: v }))}
                    >
                      <SelectTrigger className="border-[#E5E0D8]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {coursesForDate.map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.time} · {getLoc(c.location)}{c.monitors?.length ? ` · ${c.monitors.map(m => m.name).filter(Boolean).join(', ')}` : ''}
                          </SelectItem>
                        ))}
                        <SelectItem value="__new__">+ Nuevo curso para este día</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="bg-[#FFF9F0] border border-[#F2CC8F] rounded-lg px-3 py-2 text-sm text-[#8A7A5A]">
                      No hay curso para este día · se creará uno nuevo automáticamente
                    </div>
                  )}
                </div>

                {/* Time for new course (shown when no existing course OR '__new__' selected) */}
                {(coursesForDate.length === 0 || resForm.course_id === '__new__') && (
                  <div className="space-y-1">
                    <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Hora del nuevo curso</Label>
                    <Input
                      type="time"
                      value={resForm.new_course_time}
                      onChange={e => setResForm(f => ({ ...f, new_course_time: e.target.value }))}
                      required
                      className="border-[#E5E0D8] w-32"
                    />
                  </div>
                )}
              </>
            )}

            {resForm.multi_day && resForm.date_from && resForm.date_to && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Días ({(resForm.selected_dates||[]).length})</Label>
                  <div className="flex gap-2 text-xs">
                    <button type="button" onClick={() => setResForm(f => ({...f, selected_dates: getDatesInRange(f.date_from, f.date_to)}))} className="text-[#4A6D7C] hover:underline">Todos</button>
                    <span className="text-[#8A9BA8]">·</span>
                    <button type="button" onClick={() => setResForm(f => ({...f, selected_dates: []}))} className="text-[#4A6D7C] hover:underline">Ninguno</button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1">
                  {getDatesInRange(resForm.date_from, resForm.date_to).map(d => { const dt = new Date(d + 'T12:00:00'); const wd = dt.toLocaleDateString('es-ES', {weekday:'short'}); const dd = dt.getDate(); const sel = (resForm.selected_dates||[]).includes(d); return <button key={d} type="button" onClick={() => setResForm(f => { const sd = f.selected_dates||[]; return {...f, selected_dates: sel ? sd.filter(x=>x!==d) : [...sd,d].sort()}; })} className={`px-2 py-1 rounded text-xs font-medium border transition-colors ${sel ? 'bg-[#4A6D7C] text-white border-[#4A6D7C]' : 'bg-gray-100 text-gray-400 border-gray-200 line-through'}`}>{wd} {dd}</button>; })}
                </div>
              </div>
            )}

            {/* ── Common reservation fields ── */}
            <div className="border-t border-[#E5E0D8] pt-3 space-y-4">
              <CustomerAutocomplete onSelect={c => c && setResForm(f => ({ ...f, reservation_name: c.name, phone: c.phone || f.phone, email: c.email || f.email, dni: c.dni || f.dni }))} />
              <div className="space-y-1">
                <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Nombre Reserva</Label>
                <Input value={resForm.reservation_name} onChange={e => setResForm(f => ({ ...f, reservation_name: e.target.value }))} required className="border-[#E5E0D8]" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Teléfono</Label>
                  <Input value={resForm.phone} onChange={e => setResForm(f => ({ ...f, phone: e.target.value }))} className="border-[#E5E0D8]" placeholder="+34..." />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Email</Label>
                  <Input value={resForm.email} onChange={e => setResForm(f => ({ ...f, email: e.target.value }))} className="border-[#E5E0D8]" placeholder="email@..." />
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">DNI</Label>
                <Input value={resForm.dni} onChange={e => setResForm(f => ({ ...f, dni: e.target.value }))} className="border-[#E5E0D8]" />
              </div>

              <div className="space-y-1">
                <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Nº Total Alumnos</Label>
                <Input type="number" min="0" value={resForm.num_students} onChange={e => setResForm(f => ({ ...f, num_students: parseInt(e.target.value) || 0 }))} className="border-[#E5E0D8] w-32" placeholder="0" />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Alumnos (nombres)</Label>
                  <Button type="button" variant="ghost" size="sm" onClick={addStudent} className="text-xs text-[#4A6D7C]"><Plus className="w-3 h-3 mr-1" />Añadir</Button>
                </div>
                {resForm.students.map((s, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input placeholder={`Alumno ${i + 1}`} value={s.name} onChange={e => updateStudent(i, e.target.value)} className="border-[#E5E0D8] flex-1" />
                    {resForm.students.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeStudent(i)} className="h-8 w-8 text-[#E07A5F]"><Trash2 className="w-3 h-3" /></Button>
                    )}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Total (€)</Label>
                  <Input type="number" step="0.01" value={resForm.income_total} onChange={e => setResForm(f => ({ ...f, income_total: e.target.value }))} className="border-[#E5E0D8]" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Pagado (€)</Label>
                  <Input type="number" step="0.01" value={resForm.amount_paid} disabled={resForm.payment_splits?.length > 1} onChange={e => setResForm(f => ({ ...f, amount_paid: e.target.value }))} className="border-[#E5E0D8] disabled:opacity-60" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Forma de pago</Label>
                  <Select value={resForm.payment_method} onValueChange={v => setResForm(f => ({ ...f, payment_method: v }))} disabled={resForm.payment_splits?.length > 1}>
                    <SelectTrigger className="border-[#E5E0D8]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(bizumRecipients||[]).map(r => <SelectItem key={r.id} value={`bizum_${r.id}`}>Bizum {r.name}</SelectItem>)}
                      <SelectItem value="caixa">Caixa</SelectItem>
                      <SelectItem value="cash">Efectivo/Cash</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <PaymentSplitField
                paymentMethod={resForm.payment_method}
                paymentSplits={resForm.payment_splits}
                amountPaid={resForm.amount_paid}
                bizumRecipients={bizumRecipients}
                onChange={patch => setResForm(f => ({ ...f, ...patch }))}
              />

              {resForm.income_total && (
                <p className="text-sm text-[#E07A5F] font-semibold tabular-nums">
                  Pendiente: {((parseFloat(resForm.income_total) || 0) - (parseFloat(resForm.amount_paid) || 0)).toFixed(2)} €
                </p>
              )}

              <div className="space-y-1">
                <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">Notas</Label>
                <Input value={resForm.notes} onChange={e => setResForm(f => ({ ...f, notes: e.target.value }))} className="border-[#E5E0D8]" />
              </div>
            </div>

            <Button type="submit" disabled={submitting} className="w-full bg-[#81B29A] hover:bg-[#6A9A83] text-white">
              {submitting ? 'Creando...' : 'Crear Reserva'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={batchSelectDialog} onOpenChange={setBatchSelectDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-xl font-bold text-[#1A2B3C]" style={{fontFamily:'Manrope'}}>Elige el curso para cada día</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-[#8A9BA8]">Hay días con varios cursos disponibles. Elige en cuál añadir la reserva.</p>
            {pendingBatchAmbiguousDays.map(({date, options}) => (
              <div key={date} className="space-y-1">
                <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A9BA8]">{fmtDate(date)}</Label>
                <Select value={batchDaySelections[date] || ''} onValueChange={v => setBatchDaySelections(p => ({...p, [date]: v}))}>
                  <SelectTrigger className="border-[#E5E0D8]"><SelectValue placeholder="Elige curso..."/></SelectTrigger>
                  <SelectContent>
                    {options.map(c => <SelectItem key={c.id} value={c.id}>{c.time} — {getLoc(c.location)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <Button onClick={handleBatchWithSelections} className="w-full mt-4 bg-[#81B29A] hover:bg-[#6A9A83] text-white">Crear reservas</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
